import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";

/**
 * "You are here" 3D marker — a glowing sky-blue puck with a pulsing halo and
 * a floating HTML label. Only rendered when `worldPos` is provided (visitor
 * has GPS lock inside the venue).
 */
export function VisitorMarker3D({ worldPos, accuracyMeters }) {
  const haloRef = useRef();
  const puckRef = useRef();

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (haloRef.current) {
      const s = 1 + Math.sin(t * 2.6) * 0.35;   // radial pulse
      haloRef.current.scale.setScalar(s);
      haloRef.current.material.opacity = 0.35 + Math.cos(t * 2.6) * 0.15;
    }
    if (puckRef.current) {
      puckRef.current.position.y = 0.6 + Math.sin(t * 3) * 0.15;   // gentle bob
    }
  });

  const acc = useMemo(() => (accuracyMeters != null ? Math.round(accuracyMeters) : null), [accuracyMeters]);

  if (!worldPos) return null;

  return (
    <group position={[worldPos.x, 0, worldPos.z]}>
      {/* Accuracy ring — semi-transparent, sized to match GPS accuracy in meters */}
      {acc != null && acc > 0 && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.05, 0]}>
          <ringGeometry args={[Math.max(2, acc - 0.5), Math.max(2.5, acc), 64]} />
          <meshBasicMaterial color="#38bdf8" transparent opacity={0.15} />
        </mesh>
      )}

      {/* Pulsing halo */}
      <mesh ref={haloRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.1, 0]}>
        <circleGeometry args={[3.2, 48]} />
        <meshBasicMaterial color="#38bdf8" transparent opacity={0.45} />
      </mesh>

      {/* Solid puck */}
      <mesh ref={puckRef} position={[0, 0.6, 0]}>
        <cylinderGeometry args={[1.4, 1.4, 0.6, 32]} />
        <meshStandardMaterial color="#0ea5e9" emissive="#38bdf8" emissiveIntensity={0.9} />
      </mesh>

      {/* Vertical light beam so it's visible from far zoom-out */}
      <mesh position={[0, 8, 0]}>
        <cylinderGeometry args={[0.15, 0.6, 16, 12]} />
        <meshBasicMaterial color="#38bdf8" transparent opacity={0.3} />
      </mesh>

      {/* HTML label */}
      <Html
        position={[0, 4.5, 0]}
        center
        distanceFactor={16}
        occlude={false}
        style={{ pointerEvents: "none" }}
      >
        <div className="px-2 py-1 rounded-full text-[10px] font-black bg-sky-500 text-white shadow-lg shadow-sky-500/60 whitespace-nowrap uppercase tracking-widest border border-white/40">
          You are here{acc != null ? ` · ±${acc}m` : ""}
        </div>
      </Html>
    </group>
  );
}
